/**
 * Clients Page - Microsoft 365 Style
 * Manage clients with professional data table
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Input,
  Text,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Badge,
} from '@fluentui/react-components';
import {
  EditRegular,
  DeleteRegular,
  EyeRegular,
  MoreHorizontalRegular,
  SearchRegular,
  BuildingRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  PeopleRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../components/layout';
import { SearchableDropdown, TablePagination } from '../../components/common';
import { usePagination } from '../../hooks/usePagination';
import { ApiClient, newToast } from '../../configurations/index.config';
import { getErrorMessage } from '../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  dashboardCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  statLabel: {
    fontSize: '13px',
    color: '#605e5c',
  },
  filterSection: {
    marginBottom: '24px',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr) auto',
    gap: '12px',
    alignItems: 'end',
  },
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  filterLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#605e5c',
  },
  searchBar: {
    marginBottom: '16px',
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  clientCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButton: {
    minWidth: 'auto',
    padding: '6px',
  },
});

interface Client {
  id: string;
  client_name: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  category_name?: string;
  category_id?: number;
  client_type?: string;
  status?: string;
  created_at?: string;
}

// Helper function to check if a client record needs completion
const isClientIncomplete = (client: Client): boolean => {
  return !client.client_type || !client.category_id;
};

interface ClientCategory {
  id: number;
  name: string;
}

export const ClientFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [categories, setCategories] = useState<ClientCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Filter state
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClientType, setFilterClientType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // Stats
  const stats = useMemo(() => {
    const active = clients.filter(c => c.status === 'active').length;
    const inactive = clients.filter(c => c.status === 'inactive').length;
    const needsCompletion = clients.filter(c => isClientIncomplete(c)).length;
    return { active, inactive, total: clients.length, needsCompletion };
  }, [clients]);

  // Client types from data
  const clientTypes = useMemo(() => {
    const types = new Set(clients.map(c => c.client_type).filter(Boolean));
    return Array.from(types) as string[];
  }, [clients]);

  useEffect(() => {
    fetchClients();
    fetchCategories();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-clients');
      if (response.data.status === 'success') {
        setClients(response.data.data || []);
      } else {
        newToast('error', response.data?.message || 'Failed to fetch clients');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await ApiClient.get('/get-client-categories');
      if (response.data.status === 'success') {
        setCategories(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleAddClient = () => {
    navigate('/account/add-client');
  };

  const handleEditClient = (client: Client) => {
    navigate('/account/add-client', { state: { client } });
  };

  const handleViewClient = (client: Client) => {
    navigate(`/account/client-details/${client.id}`, { state: { client } });
  };

  const handleDeleteClient = async () => {
    if (!selectedClient) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-client', {
        id: selectedClient.id,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Client deleted successfully');
        fetchClients();
      } else {
        newToast('error', response.data?.message || 'Failed to delete client');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setDeleteDialogOpen(false);
    setSelectedClient(null);
  };

  const filteredClients = useMemo(() => {
    let filtered = [...clients];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        client =>
          client.client_name?.toLowerCase().includes(query) ||
          client.contact_name?.toLowerCase().includes(query) ||
          client.email?.toLowerCase().includes(query) ||
          client.phone?.includes(query) ||
          client.category_name?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (filterStatus) {
      filtered = filtered.filter(client => client.status === filterStatus);
    }

    // Client type filter
    if (filterClientType) {
      filtered = filtered.filter(client => client.client_type === filterClientType);
    }

    // Category filter
    if (filterCategory) {
      filtered = filtered.filter(client => client.category_name === filterCategory);
    }

    // Date range filter
    if (filterStartDate) {
      filtered = filtered.filter(
        client => client.created_at && new Date(client.created_at) >= new Date(filterStartDate)
      );
    }
    if (filterEndDate) {
      filtered = filtered.filter(
        client => client.created_at && new Date(client.created_at) <= new Date(filterEndDate)
      );
    }

    return filtered;
  }, [
    clients,
    searchQuery,
    filterStatus,
    filterClientType,
    filterCategory,
    filterStartDate,
    filterEndDate,
  ]);

  const clearFilters = () => {
    setFilterStatus('');
    setFilterClientType('');
    setFilterCategory('');
    setFilterStartDate('');
    setFilterEndDate('');
    setSearchQuery('');
  };

  const hasActiveFilters =
    filterStatus ||
    filterClientType ||
    filterCategory ||
    filterStartDate ||
    filterEndDate ||
    searchQuery;

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) {
      return '—';
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Pagination
  const { currentPage, itemsPerPage, paginatedData, totalItems, setCurrentPage, setItemsPerPage } =
    usePagination({
      data: filteredClients,
      initialPage: 1,
      initialItemsPerPage: 10,
    });

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Manage Clients</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${clients.length} total clients`}
        </Text>
      </div>

      {/* Command Bar */}
      <TableCommandBar onAdd={handleAddClient} addLabel="Add Client" />

      {/* Dashboard Cards */}
      <div className={styles.dashboardCards}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#e8f5e9', color: '#388e3c' }}>
            <CheckmarkCircleRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.active}</Text>
            <Text className={styles.statLabel}>Active Clients</Text>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#ffebee', color: '#d32f2f' }}>
            <DismissCircleRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.inactive}</Text>
            <Text className={styles.statLabel}>Inactive Clients</Text>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}>
            <PeopleRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.total}</Text>
            <Text className={styles.statLabel}>Total Clients</Text>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#fff8e1', color: '#f9a825' }}>
            <WarningRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.needsCompletion}</Text>
            <Text className={styles.statLabel}>Needs Completion</Text>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <ContentCard>
        <div className={styles.filterSection}>
          <Text
            style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'block' }}
          >
            Filter Clients
          </Text>
          <div className={styles.filterGrid}>
            <div className={styles.filterField}>
              <Text className={styles.filterLabel}>Status</Text>
              <SearchableDropdown
                placeholder="All statuses"
                options={[
                  { id: 'active', label: 'Active' },
                  { id: 'inactive', label: 'Inactive' },
                ]}
                selectedValue={filterStatus}
                onSelect={value => setFilterStatus(value)}
              />
            </div>

            <div className={styles.filterField}>
              <Text className={styles.filterLabel}>Client Type</Text>
              <SearchableDropdown
                placeholder="All types"
                options={clientTypes.map(t => ({ id: t, label: t }))}
                selectedValue={filterClientType}
                onSelect={value => setFilterClientType(value)}
              />
            </div>

            <div className={styles.filterField}>
              <Text className={styles.filterLabel}>Category</Text>
              <SearchableDropdown
                placeholder="All categories"
                options={categories.map(c => ({ id: c.name, label: c.name }))}
                selectedValue={filterCategory}
                onSelect={value => setFilterCategory(value)}
              />
            </div>

            <div className={styles.filterField}>
              <Text className={styles.filterLabel}>From Date</Text>
              <Input
                type="date"
                value={filterStartDate}
                onChange={e => setFilterStartDate(e.target.value)}
              />
            </div>

            <div className={styles.filterField}>
              <Text className={styles.filterLabel}>To Date</Text>
              <Input
                type="date"
                value={filterEndDate}
                onChange={e => setFilterEndDate(e.target.value)}
              />
            </div>

            {hasActiveFilters && (
              <Button appearance="secondary" onClick={clearFilters}>
                Clear
              </Button>
            )}
          </div>
        </div>
      </ContentCard>

      {/* Search Bar */}
      <div className={styles.searchBar} style={{ marginTop: '24px' }}>
        <Input
          placeholder="Search clients..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          contentBefore={<SearchRegular />}
          size="medium"
        />
      </div>

      {/* Clients Table */}
      <ContentCard noPadding>
        {loading ? (
          <div className={styles.emptyState}>
            <Text>Loading clients...</Text>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className={styles.emptyState}>
            <BuildingRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No clients found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              {hasActiveFilters
                ? 'Try adjusting your filters'
                : 'Click "Add Client" to get started'}
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Client Name</th>
                <th className={styles.th}>Contact Person</th>
                <th className={styles.th}>Email</th>
                <th className={styles.th}>Phone</th>
                <th className={styles.th}>Category</th>
                <th className={styles.th}>Client Type</th>
                <th className={styles.th}>Status</th>
                <th className={styles.th}>Date Added</th>
                <th className={styles.th} style={{ width: '80px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map(client => (
                <tr
                  key={client.id}
                  className={styles.tr}
                  style={isClientIncomplete(client) ? { backgroundColor: '#fff8e1' } : undefined}
                >
                  <td className={styles.td}>
                    <div className={styles.clientCell}>
                      <Avatar
                        name={client.client_name}
                        color="colorful"
                        size={32}
                        icon={<BuildingRegular />}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Text weight="semibold">{client.client_name}</Text>
                        {isClientIncomplete(client) && (
                          <Badge
                            appearance="tint"
                            color="warning"
                            icon={<WarningRegular />}
                            size="small"
                          >
                            Needs Completion
                          </Badge>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={styles.td}>{client.contact_name || '—'}</td>
                  <td className={styles.td}>{client.email || '—'}</td>
                  <td className={styles.td}>{client.phone || '—'}</td>
                  <td className={styles.td}>
                    {client.category_name ? (
                      <Badge appearance="tint" color="brand">
                        {client.category_name}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={styles.td}>{client.client_type || '—'}</td>
                  <td className={styles.td}>
                    {client.status === 'active' ? (
                      <Badge appearance="filled" color="success">
                        Active
                      </Badge>
                    ) : (
                      <Badge appearance="filled" color="danger">
                        Inactive
                      </Badge>
                    )}
                  </td>
                  <td className={styles.td}>{formatDate(client.created_at)}</td>
                  <td className={styles.td}>
                    <Menu>
                      <MenuTrigger>
                        <Button
                          icon={<MoreHorizontalRegular />}
                          appearance="subtle"
                          className={styles.actionButton}
                        />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem icon={<EyeRegular />} onClick={() => handleViewClient(client)}>
                            View
                          </MenuItem>
                          <MenuItem icon={<EditRegular />} onClick={() => handleEditClient(client)}>
                            Edit
                          </MenuItem>
                          <MenuItem
                            icon={<DeleteRegular />}
                            onClick={() => {
                              setSelectedClient(client);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filteredClients.length > 0 && (
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </ContentCard>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Delete Client</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <strong>{selectedClient?.client_name}</strong>? This
              action cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDeleteClient}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ClientFluentUI;
